import React from "react";
import {
  AssetType,
  PrintMetadata,
  AssetFormData,
} from "../../../types/asset-types";
import {
  BaseAssetFormProps,
  AssetFormHeader,
  FormField,
  TextInput,
  SelectInput,
  NumberInput,
  AIConversationFields,
} from "./asset-form-base";

const PUBLICATION_TYPE_OPTIONS = [
  { value: "newspaper", label: "Newspaper" },
  { value: "magazine", label: "Magazine" },
  { value: "brochure", label: "Brochure" },
  { value: "flyer", label: "Flyer" },
  { value: "other", label: "Other" },
];

export function PrintAssetForm({
  data,
  onChange,
  errors,
  disabled,
}: BaseAssetFormProps) {
  const metadata = (data.metadata as Partial<PrintMetadata>) || {};

  const handleMetadataChange = (field: keyof PrintMetadata, value: any) => {
    const newMetadata = {
      ...metadata,
      [field]: value,
    };
    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  const handleAIContentChange = (aiData: any) => {
    const newMetadata = {
      ...metadata,
      ...aiData,
    };
    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  return (
    <div className="space-y-6">
      <AssetFormHeader
        type={AssetType.PRINT}
        description="Create a print asset like a newspaper ad, magazine insert, brochure, or flyer."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Publication"
          required
          error={errors?.["metadata.publication"]}
          description="Name of the publication or print medium"
        >
          <TextInput
            value={metadata.publication || ""}
            onChange={(value) => handleMetadataChange("publication", value)}
            placeholder="New York Times, Vogue Magazine, Company Brochure"
            disabled={disabled}
            required
          />
        </FormField>

        <FormField
          label="Publication Type"
          required
          error={errors?.["metadata.publicationType"]}
          description="Type of print publication"
        >
          <SelectInput
            value={metadata.publicationType || ""}
            onChange={(value) => handleMetadataChange("publicationType", value)}
            options={PUBLICATION_TYPE_OPTIONS}
            placeholder="Select publication type"
            disabled={disabled}
            required
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Issue Date"
          error={errors?.["metadata.issueDate"]}
          description="Publication date (for newspapers, magazines, etc.)"
        >
          <TextInput
            value={metadata.issueDate || ""}
            onChange={(value) => handleMetadataChange("issueDate", value)}
            placeholder="2024-01-15 or January 2024"
            disabled={disabled}
            type="date"
          />
        </FormField>

        <FormField
          label="Page Number"
          error={errors?.["metadata.pageNumber"]}
          description="Page number where the asset appears"
        >
          <NumberInput
            value={metadata.pageNumber}
            onChange={(value) => handleMetadataChange("pageNumber", value)}
            placeholder="12"
            disabled={disabled}
            min={1}
          />
        </FormField>
      </div>

      <FormField
        label="Ad Size"
        required
        error={errors?.["metadata.adSize"]}
        description="Size or dimensions of the advertisement"
      >
        <TextInput
          value={metadata.adSize || ""}
          onChange={(value) => handleMetadataChange("adSize", value)}
          placeholder="Full page, Half page, Quarter page, 4x6 inches"
          disabled={disabled}
          required
        />
      </FormField>

      <FormField
        label="Circulation"
        error={errors?.["metadata.circulation"]}
        description="Estimated circulation or distribution numbers"
      >
        <NumberInput
          value={metadata.circulation}
          onChange={(value) => handleMetadataChange("circulation", value)}
          placeholder="50000"
          disabled={disabled}
          min={0}
        />
      </FormField>

      <AIConversationFields
        data={metadata}
        onChange={handleAIContentChange}
        errors={errors}
        disabled={disabled}
      />
    </div>
  );
}
